// @flow strict

import { parseDate } from '@ta/platform.travel-info-parsing';

export type AttractionDatesClearRecord = {|
  +key: 'AC',
|};

export type AttractionRangeDatesClearRecord = {|
  +key: 'ARC',
|};

export type AttractionDatesInfoRecord = {|
  +key: 'AD',
  +tourDate: null | Date,
  +locationId: null | number,
|};

export type AttractionDatesDefaultRecord = {|
  +key: 'ADD',
  +tourDate: null | Date,
|};

export type AttractionRangeDatesInfoRecord = {|
  +key: 'ARD',
  +fromDate: null | Date,
  +toDate: null | Date,
  +locationId: null | number,
|};

export type AttractionRangeDatesDefaultRecord = {|
  +key: 'ARDD',
  +fromDate: null | Date,
  +toDate: null | Date,
|};

export type AttractionPaxRecord = {|
  +key: 'APAX',
  +adults: number,
  +children: number,
|};

export type RestaurantDatesInfoRecord = {|
  +key: 'RD',
  +date: null | Date,
  +locationId: null | number,
|};

export type RestaurantDatesDefaultRecord = {|
  +key: 'RDD',
  +date: Date,
|};

export type FlightsRoundTripDatesRecord = {|
  +key: 'RTD',
  +departureDate: Date,
  +returnDate: Date,
|};

export type FlightsOneWayDatesRecord = {|
  +key: 'OWD',
  +date: Date,
|};

/* 

We're currently not serializing to cookies from this codepath, though we could in the future
should we decide server-side tracking of this information is not worthwhile.

const serializers = {
  // eslint-disable-next-line no-unused-vars
  AC: (_: AttractionDatesClearRecord): string => '',
  // eslint-disable-next-line no-unused-vars
  ARC: (_: AttractionRangeDatesClearRecord): string => '',
  AD: (record: AttractionDatesInfoRecord): string =>
    `${serializeDate(record.tourDate)}${record.locationId === null ? '' : `.${record.locationId}`}`,
  ADD: (record: AttractionDatesDefaultRecord): string => serializeDate(record.tourDate),
  ARD: (record: AttractionRangeDatesInfoRecord): string =>
    `${serializeDate(record.fromDate)}.${serializeDate(record.toDate)}${
      record.locationId === null ? '' : `.${record.locationId}`
    }`,
  ARDD: (record: AttractionRangeDatesDefaultRecord): string =>
    `${serializeDate(record.fromDate)}.${serializeDate(record.toDate)}`,
  APAX: (record: AttractionPaxRecord): string => `${record.adults}.${record.children}`,
};

*/

export default {
  AC: (): AttractionDatesClearRecord => ({ key: 'AC' }),
  ARC: (): AttractionRangeDatesClearRecord => ({ key: 'ARC' }),
  AD: (stamp: number, data: string): null | AttractionDatesInfoRecord => {
    const fields = data.split('.');
    return {
      key: 'AD',
      tourDate: parseDate(fields[0]),
      locationId: fields.length > 1 ? parseInt(fields[1], 10) || null : null,
    };
  },
  ADD: (stamp: number, data: string): null | AttractionDatesDefaultRecord => {
    const fields = data.split('.');
    return { key: 'ADD', tourDate: parseDate(fields[0]) || null };
  },
  ARD: (stamp: number, data: string): null | AttractionRangeDatesInfoRecord => {
    const fields = data.split('.');
    if (fields.length < 2) {
      return null;
    }
    return {
      key: 'ARD',
      fromDate: parseDate(fields[0]),
      toDate: parseDate(fields[1]),
      locationId: fields.length > 2 ? parseInt(fields[2], 10) || null : null,
    };
  },
  ARDD: (stamp: number, data: string): null | AttractionRangeDatesDefaultRecord => {
    const fields = data.split('.');
    if (fields.length < 2) {
      return null;
    }
    return { key: 'ARDD', fromDate: parseDate(fields[0]), toDate: parseDate(fields[1]) };
  },
  APAX: (stamp: number, data: string): null | AttractionPaxRecord => {
    const fields = data.split('.');
    if (fields.length < 2) {
      return null;
    }
    return {
      key: 'APAX',
      adults: parseInt(fields[0], 10) || 2,
      children: parseInt(fields[1], 10) || 0,
    };
  },
  RD: (stamp: number, data: string): null | RestaurantDatesInfoRecord => {
    const fields = data.split('.');
    return {
      key: 'RD',
      date: parseDate(fields[0]),
      locationId: fields.length > 1 ? parseInt(fields[1], 10) || null : null,
    };
  },
  RDD: (stamp: number, data: string): null | RestaurantDatesDefaultRecord => {
    const fields = data.split('.');
    const date = parseDate(fields[0]);
    return date === null
      ? null
      : {
          key: 'RDD',
          date,
        };
  },
  RTD: (stamp: number, data: string): null | FlightsRoundTripDatesRecord => {
    const fields = data.split('.');
    const departureDate = parseDate(fields[0]);
    const returnDate = parseDate(fields[1]);
    return departureDate === null || returnDate === null
      ? null
      : {
          key: 'RTD',
          departureDate,
          returnDate,
        };
  },
  OWD: (stamp: number, data: string): null | FlightsOneWayDatesRecord => {
    const fields = data.split('.');
    const date = parseDate(fields[0]);
    return date === null
      ? null
      : {
          key: 'OWD',
          date,
        };
  },
};
